/**
 * SISTEMA DE HITBOXES DINÁMICAS
 * Maneja el cálculo, detección y visualización de áreas clickeables
 */

import { calculateButtonScreenArea, convertClickToRelative } from '@utils/mathUtils';
import { detectClickedArea } from '@utils/geometryUtils';
import { createSVGElement, removeElements, safeQuerySelector } from '@utils/domUtils';
import { STATIC_AREAS, BUTTON_NAMES, HITBOX_CONFIG } from '@config/constants';
import { StateManager } from './StateManager';
import type { 
  ClickableArea, 
  AFrameElement, 
  AppState,
  DOMElements,
  Point2D
} from '@types';

export class HitboxSystem {
  private stateManager: StateManager;
  public showCalibration: boolean = false;
  private hitboxOverlays: (HTMLElement | SVGElement)[] = [];
  private updateInterval: number | null = null;
  private elements: DOMElements = {};

  constructor(stateManager: StateManager) {
    this.stateManager = stateManager;
    this.initializeElements();
    this.setupStateChangeListener();
  }

  /**
   * Inicializa referencias a elementos
   */
  private initializeElements(): void {
    this.elements.scene = safeQuerySelector('a-scene');
    this.elements.camera = safeQuerySelector('[camera]');
    this.elements.qrMarker = safeQuerySelector('#qrMarker');
    
    if (!this.elements.scene) {
      console.error('HitboxSystem: Escena A-Frame no encontrada');
    }
  }

  /**
   * 📢 Configura listener para cambios de estado
   */
  private setupStateChangeListener(): void {
    this.stateManager.onStateChange(() => {
      this.updateCalibrationDisplay();
    });
  }

  /**
   * Detecta qué botón fue clickeado
   */
  detectClickedButton(event: MouseEvent | TouchEvent): string | null {
    if (!this.isMarkerVisible()) {
      console.log('Marcador no visible');
      return null;
    }

    const sceneElement = this.elements.scene as HTMLElement;
    if (!sceneElement) {
      console.warn('Elemento de escena no encontrado');
      return null;
    }

    // Convertir coordenadas del click
    const clickData = convertClickToRelative(event, sceneElement);
    if (!clickData || !clickData.relative) {
      console.warn('No se pudieron obtener las coordenadas relativas del click');
      return null;
    }

    const { x, y } = clickData.relative;

    console.log(`Verificando click en: x=${x.toFixed(3)}, y=${y.toFixed(3)}, estado=${this.stateManager.getCurrentState()}`);

    // Obtener áreas dinámicas de botones
    const buttonAreas = this.getDynamicButtonAreas();
    
    // Detectar área clickeada
    const clickedArea = detectClickedArea(x, y, buttonAreas);
    
    if (clickedArea) {
      console.log(`Click detectado en ${clickedArea.id} usando ${clickedArea.detectionMethod}`);
      return clickedArea.id;
    }

    console.log('Click fuera de todas las áreas de botones');
    return null;
  }

  /**
   * Verifica si el marcador Hiro está visible
   */
  private isMarkerVisible(): boolean {
    const qrMarker = this.elements.qrMarker as AFrameElement;
    return qrMarker && qrMarker.object3D.visible;
  }

  /**
   * Verifica si un elemento y todos sus padres son visibles
   */
  private isElementAndParentsVisible(element: AFrameElement): boolean {
    let currentElement = element;
    
    while (currentElement) {
      // Verificar visibilidad del objeto3D
      if (!currentElement.object3D || !currentElement.object3D.visible) {
        return false;
      }
      
      // Verificar atributo visible de A-Frame
      const visibleAttr = currentElement.getAttribute('visible');
      if (visibleAttr === 'false' || visibleAttr === false) {
        return false;
      }
      
      // Subir al elemento padre
      currentElement = currentElement.parentElement as AFrameElement;
      
      // Parar en el marcador Hiro o en la escena
      if (currentElement && (currentElement.tagName === 'A-MARKER' || currentElement.tagName === 'A-SCENE')) {
        break;
      }
    }
    
    return true;
  }

  /**
   * Obtiene las áreas dinámicas de los botones
   */
  private getDynamicButtonAreas(): ClickableArea[] {
    const areas: ClickableArea[] = [];
    
    try {
      const cameraElement = this.elements.camera as AFrameElement;
      const sceneElement = this.elements.scene as HTMLElement;
            
      // Verificar componentes necesarios
      if (!cameraElement || !cameraElement.components.camera) {
        return this.getStaticButtonAreas();
      }

      const camera = cameraElement.components.camera.camera;
      const canvas = sceneElement.querySelector('canvas');
      const currentState = this.stateManager.getCurrentState();

      if (!canvas) {
        return this.getStaticButtonAreas();
      }

      // Calcular áreas según el estado actual
      const buttonSelectors = this.getButtonSelectorsForState(currentState);
      
      for (const [buttonId, selector] of Object.entries(buttonSelectors)) {
        const buttonElement = safeQuerySelector(selector) as AFrameElement;
        
        if (buttonElement) {
          // Verificar visibilidad del elemento y sus padres
          const isElementVisible = this.isElementAndParentsVisible(buttonElement);
          
          if (isElementVisible) {
            const area = calculateButtonScreenArea(buttonElement, camera, canvas);
            
            if (area) {
              areas.push({
                id: buttonId,
                ...area
              });
            }
          }
        }
      }

      return areas.length > 0 ? areas : this.getStaticButtonAreas();
      
    } catch (error) {
      console.log('Error calculando áreas dinámicas, usando estáticas:', error);
      return this.getStaticButtonAreas();
    }
  }

  /**
   * Obtiene selectores de botones según el estado
   */
  private getButtonSelectorsForState(state: AppState): Record<string, string> {
    const selectors: Record<AppState, Record<string, string>> = {
      home: {
        facephiButton: '#facephiButton',
        webButton: '#webButton',
        linkedinButton: '#linkedinButton',
        locationButton: '#locationButton',
        solutionButton: '#solutionButton',
      },
      solutions: {
        auButton: '#auButton',
        isButton: '#isButton',
        onButton: '#onButton',
        goHomeButton: '#goHomeButton'
      },
      initial: {}
    };

    return selectors[state] || {};
  }

  /**
   * Obtiene áreas estáticas como fallback
   */
  private getStaticButtonAreas(): ClickableArea[] {
    const currentState = this.stateManager.getCurrentState();
    const stateAreas = STATIC_AREAS[currentState] || {};
    const buttonSelectors = this.getButtonSelectorsForState(currentState);
    
    // Filtrar solo botones visibles
    const visibleAreas: ClickableArea[] = [];
    
    for (const [buttonId, area] of Object.entries(stateAreas)) {
      const selector = buttonSelectors[buttonId];
      if (selector) {
        const buttonElement = safeQuerySelector(selector) as AFrameElement;
        if (buttonElement && this.isElementAndParentsVisible(buttonElement)) {
          visibleAreas.push({
            ...(area as ClickableArea),
            id: buttonId
          });
        }
      }
    }
    
    return visibleAreas;
  }

  /**
   * Muestra/oculta las hitboxes de calibración
   */
  toggleCalibration(show: boolean = !this.showCalibration): void {
    this.showCalibration = show;
    
    if (this.showCalibration) {
      this.showHitboxes();
      this.startDynamicUpdate();
    } else {
      this.hideHitboxes();
      this.stopDynamicUpdate();
    }
    
    console.log(`Calibración ${this.showCalibration ? 'activada' : 'desactivada'}`);
  }

  /**
   * Actualiza la visualización de calibración
   */
  updateCalibrationDisplay(): void {
    if (this.showCalibration) {
      this.hideHitboxes();
      this.showHitboxes();
    }
  }

  /**
   * Muestra las hitboxes visuales
   */
  private showHitboxes(): void {
    const areas = this.getDynamicButtonAreas();
    const sceneElement = this.elements.scene as HTMLElement;
    
    if (!sceneElement) return;

    const sceneRect = sceneElement.getBoundingClientRect();
    
    areas.forEach(area => {
      const overlay = this.createHitboxOverlay(area, sceneRect);
      if (overlay) {
        document.body.appendChild(overlay);
        this.hitboxOverlays.push(overlay);
      }
    });
  }

  /**
   * Oculta las hitboxes visuales
   */
  private hideHitboxes(): void {
    removeElements(this.hitboxOverlays);
    this.hitboxOverlays = [];
  }

  /**
   * Crea overlay visual para una hitbox
   */
  private createHitboxOverlay(area: ClickableArea, sceneRect: DOMRect): HTMLElement | null {
    if (!area.corners || area.corners.length < 3) {
      return null;
    }

    // Crear contenedor del overlay
    const overlay = document.createElement('div');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9999;
    `;

    // Crear SVG para el polígono
    const svg = createSVGElement('svg', {
      width: '100%',
      height: '100%',
      style: 'position: absolute; top: 0; left: 0;'
    });

    // Convertir coordenadas relativas a píxeles absolutos
    const absoluteCorners = area.corners.map((corner: Point2D) => ({
      x: sceneRect.left + corner.x * sceneRect.width,
      y: sceneRect.top + corner.y * sceneRect.height
    }));

    // Crear polígono
    const polygon = createSVGElement('polygon', {
      points: absoluteCorners.map((p: Point2D) => `${p.x},${p.y}`).join(' '),
      fill: HITBOX_CONFIG.OVERLAY_STYLE?.fill || 'rgba(255, 0, 0, 0.3)',
      stroke: HITBOX_CONFIG.OVERLAY_STYLE?.stroke || '#ff0000',
      'stroke-width': HITBOX_CONFIG.OVERLAY_STYLE?.strokeWidth || 2
    });

    // Crear texto con el nombre del botón
    const centerX = absoluteCorners.reduce((sum: number, p: Point2D) => sum + p.x, 0) / absoluteCorners.length;
    const centerY = absoluteCorners.reduce((sum: number, p: Point2D) => sum + p.y, 0) / absoluteCorners.length;
    
    const text = createSVGElement('text', {
      x: centerX,
      y: centerY,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      fill: '#ffffff',
      'font-size': '14px',
      'font-weight': 'bold'
    });
    text.textContent = BUTTON_NAMES[area.id] || area.id;

    svg.appendChild(polygon);
    svg.appendChild(text);
    overlay.appendChild(svg);

    return overlay;
  }

  /**
   * Inicia actualización dinámica de hitboxes
   */
  private startDynamicUpdate(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    
    this.updateInterval = window.setInterval(() => {
      this.updateCalibrationDisplay();
    }, HITBOX_CONFIG.UPDATE_INTERVAL);
  }

  /**
   * Detiene actualización dinámica
   */
  private stopDynamicUpdate(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
  }

  /**
   * Limpia recursos del sistema
   */
  cleanup(): void {
    this.stopDynamicUpdate();
    this.hideHitboxes();
    console.log('HitboxSystem limpiado');
  }
}